/** Share observers by scroll root and margin; release each empty group. */
type PendingImage = { callback: () => void; cancel: () => void };
type ImageGroup = { observer: IntersectionObserver; pending: Map<Element, PendingImage> };
const roots = new Map<Element | null, Map<string, ImageGroup>>();

function observeInRoot(element: Element, root: Element | null, rootMargin: string, callback: () => void): () => void {
  let groups = roots.get(root);
  if (!groups) { groups = new Map(); roots.set(root, groups); }
  let group = groups.get(rootMargin);
  if (!group) {
    const pending = new Map<Element, PendingImage>();
    const observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const image = pending.get(entry.target);
        if (!image) continue;
        // Release before invoking application code; callbacks may destroy views.
        image.cancel();
        image.callback();
      }
    }, { root, rootMargin, threshold: 0.01 });
    group = { observer, pending };
    groups.set(rootMargin, group);
  }
  const current = group;
  current.pending.get(element)?.cancel();
  // A replacement may have removed the last pending entry from this group/root.
  roots.set(root, groups);
  groups.set(rootMargin, current);
  const image: PendingImage = { callback, cancel: () => {
    if (current.pending.get(element) !== image) return;
    current.pending.delete(element);
    current.observer.unobserve(element);
    if (!current.pending.size) {
      current.observer.disconnect();
      if (groups.get(rootMargin) === current) groups.delete(rootMargin);
      if (!groups.size && roots.get(root) === groups) roots.delete(root);
    }
  } };
  current.pending.set(element, image);
  current.observer.observe(element);
  return image.cancel;
}

export function observeImageOnce(element: Element, rootMargin: string, callback: () => void): () => void {
  // The viewport's rootMargin cannot expand an intermediate overflow clip.
  // Ionic owns the actual scroll element in its shadow DOM; use its public API.
  const content = element.closest('ion-content') as (Element & { getScrollElement?: () => Promise<HTMLElement> }) | null;
  let cancelled = false;
  let stop: (() => void) | undefined;
  const start = (root: Element | null) => {
    if (!cancelled) stop = observeInRoot(element, root, rootMargin, callback);
  };
  if (content?.getScrollElement) void content.getScrollElement().then(start, () => start(null));
  else start(null);
  return () => { cancelled = true; stop?.(); };
}
